// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { emptyColor, homeGray, subheadGrey } from "components/shell/presentation/AppStyles";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useSlotsForSuggestions } from "hooks/SlotsForSuggestions";
import { useChunkSelection } from "hooks/chunkSelection";
import { useAppSelector } from "hooks/redux";
import { useAddBinding } from "hooks/reports";
import { useWaitCursor } from "hooks/WaitCursor";
import { useEffect, useMemo, useRef, useState } from "react";
import { selectReportDocumentId, selectReportEntryBindingsForCurrentDocument, selectReportId } from "store/reports/selectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { ItemSlots } from "./ItemSlots";
import { SlidingPanel } from "./SlidingPanel";

const SuggestionsButton = styled(DgButton)`
    font-family: ${dgFontFamily};
    color: ${homeGray};
    font-weight: 500;
    font-size: 10px;
    background: ${subheadGrey};
    display: flex;
    flex-direction: row;
    padding: 5px 7px;
    gap: 6px;
    border-radius: 2px;
    line-height: 100%;
    height: 20px;
    &:hover {
        background-color: ${(props: any) => props.disabled ? '' : emptyColor};
    }

    &:active:hover {
        background-color: ${subheadGrey};
    }
`

const ToggleSuggestionsButton = styled(SuggestionsButton)`
    width: 150px;
    margin: auto;
`

const AddSuggestionsButton = styled(SuggestionsButton)`
    margin-left: auto;
    width: fit-content;
    min-width: max-content;
`

const HelpText = styled.span`
    font-family: ${dgFontFamily};
    font-size: 9px;
    font-weight: 500;
    padding: 0px 10px 0px 5px;
`

const Root = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 7px;
    .suggestionList {
        max-height: 250px;
    }
`

const ListRoot = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 5px;
`

const CommandsRoot = styled.div`
    display: flex;
    width: 100%;
    height: 25px;
`

const helpText = 'The items you select will be added to the card. You can remove them at any time.'

export interface CardBindingSuggestionsProps {
    entryId: string;
    isEntrySelected?: boolean;
}

export const CardBindingSuggestions = (props: CardBindingSuggestionsProps): JSX.Element | null => {
    const [isOpen, setIsOpen] = useState(false);

    // We only want the suggestions UX to be available if it's already open (as you may have a suggestion selected which would deselect the current entry)
    // or the entry is currently selected.  Once the entry is no longer selected and the suggestions UX has been dismissed it will go away.
    const enabled = isOpen || !!props.isEntrySelected;
    return enabled ?
        <BindingSuggestionsList entryId={props.entryId} onShow={() => setIsOpen(true)} onHide={() => setIsOpen(false)} /> :
        null
}

interface BindingSuggestionsListProps {
    entryId: string;
    onShow?: () => void;
    onHide?: () => void;
}

const BindingSuggestionsList = (props: BindingSuggestionsListProps): JSX.Element | null => {
    const reportId = useAppSelector(selectReportId)
    const folderId = useAppSelector(selectReportDocumentId) ?? '';
    const entryBindings = useAppSelector(state => selectReportEntryBindingsForCurrentDocument(state)?.entities[props.entryId]);

    const [isOpen, setIsOpen] = useState(false);
    const showCallbackRef = useRef(props.onShow);

    const { selectChunk } = useChunkSelection();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);

    const suggestions = useMemo(() => {
        if (!entryBindings?.suggestions?.length) {
            return [];
        }

        const allSuggestions: string[] = [];
        for (const suggestion of entryBindings.suggestions) {
            if (!allSuggestions.some(x => x === suggestion.spanId) && !entryBindings.bindings.some(x => x.spanId === suggestion.spanId)) {
                allSuggestions.push(suggestion.spanId!);
            }
        }
        return allSuggestions;
    }, [entryBindings?.bindings, entryBindings?.suggestions]);

    const { trigger: triggerAddBinding } = useAddBinding(reportId);

    useEffect(() => {
        showCallbackRef.current = props.onShow;
    }, [props.onShow]);

    useEffect(() => {
        if (!selectedChunkInstance || (!!selectedChunkInstance && !suggestions.some(x => x === selectedChunkInstance.spanId) && !entryBindings?.bindings.some(x => x.spanId === selectedChunkInstance.spanId))) {
            setIsOpen(false);
        }
    }, [selectedChunkInstance, suggestions, entryBindings?.bindings])

    async function add(ids: string[]) {
        // We can make this a bulk request in the future - for now we add each individually
        const promises = ids.map(x => triggerAddBinding({
            folderId: folderId,
            entryId: props.entryId,
            instanceId: x
        }));

        await Promise.allSettled(promises);

        setIsOpen(false);
    }

    function onToggleClose() {
        // Closing via toggle button so select one of the primary bindings for the entry as a default selection
        if (!!selectedChunkInstance && !!entryBindings?.bindings.length && suggestions.some(x => x === selectedChunkInstance.spanId)) {
            selectChunk(entryBindings.bindings[0].spanId!);
        }
    }

    return (
        <CardSuggestionsList
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            suggestions={suggestions}
            add={add}
            onShow={props.onShow}
            onHide={props.onHide}
            onClose={onToggleClose} />
    )
}

interface CardSuggestionsListProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    suggestions: string[];
    add: (ids: string[]) => Promise<void>;
    onClose?: () => void;
    onShow?: () => void;
    onHide?: () => void;
}

const CardSuggestionsList = (props: CardSuggestionsListProps) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const showCallbackRef = useRef(props.onShow);

    const { selectChunk } = useChunkSelection();

    const { execute: executeAddSuggestions, state: addSuggestionsState } = useAsyncHelper<void>("add suggestions");
    useWaitCursor(addSuggestionsState === RequestState.InProgress);

    const { suggestions } = props;
    // Turn suggestions to slot items to be displayed in the list
    const slots = useSlotsForSuggestions(suggestions);

    useEffect(() => {
        showCallbackRef.current = props.onShow;
    }, [props.onShow]);

    async function add() {
        if (selectedIds.length === 0) {
            return;
        }

        await props.add(selectedIds)

        setSelectedIds([]);
        props.setIsOpen(false);
    }

    if (!suggestions.length) {
        return null;
    }

    return (
        <Root>
            <ToggleSuggestionsButton
                onClick={() => {
                    if (!props.isOpen) {
                        props.onShow?.();
                    }

                    if (props.isOpen) {
                        props.onClose?.();
                    }

                    props.setIsOpen(!props.isOpen);
                }}
                data-automation-id={props.isOpen ? AutomationId.WorkPathHideMoreLikeThisButton : AutomationId.WorkPathShowMoreLikeThisButton}
            >
                <Icon iconName={props.isOpen ? "ChevronUpSmall" : "ChevronDownSmall"} />{props.isOpen ? 'HIDE' : 'SHOW'} MORE LIKE THIS
            </ToggleSuggestionsButton>
            <SlidingPanel isOpen={props.isOpen} onAnimationComplete={panelOpen => {
                if (!panelOpen) {
                    // After the close animation completes notify that the suggestions can be hidden
                    props.onHide?.();
                    setSelectedIds([]);
                }
            }}>
                <ListRoot>
                    <ItemSlots
                        slots={slots}
                        hideManagementCommands={true}
                        onClick={item => selectChunk(item.id)}
                        listWrapperClassName="suggestionList"
                        selectionOptions={{
                            title: `${slots.length} candidate${slots.length === 1 ? '' : 's'} found`,
                            selectedIds: selectedIds,
                            onSelectionChanged: x => setSelectedIds(x),
                            multiselect: true
                        }} />
                    <CommandsRoot>
                        {selectedIds.length === 0 && <HelpText>{helpText}</HelpText>}
                        <AddSuggestionsButton disabled={!selectedIds.length} onClick={() => executeAddSuggestions(add())}><Icon iconName="Add" />ADD SELECTED</AddSuggestionsButton>
                    </CommandsRoot>
                </ListRoot>
            </SlidingPanel>
        </Root>
    )
}
